import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_industrial-shields/artifacts/otud2dwa_Flexi.png";

const links = [
  { id: "inicio", label: "Inicio" },
  { id: "nosotros", label: "Nosotros" },
  { id: "servicios", label: "Servicios", dropdown: true },
  { id: "galeria", label: "Galería" },
  { id: "productos", label: "Tienda" },
  { id: "contacto", label: "Contacto" },
];

const servicePages = [
  {
    to: "/fabricacion-de-guardas-telescopicas-cnc",
    label: "Fabricación CNC",
    desc: "Guardas y fuelles a medida",
    testid: "nav-submenu-fabricacion",
    mobileTestid: "nav-mobile-link-fabricacion-cnc",
  },
  {
    to: "/reparacion-de-guardas-telescopicas-cnc",
    label: "Reparación CNC",
    desc: "Diagnóstico y urgencias 24/7",
    testid: "nav-submenu-reparacion",
    mobileTestid: "nav-mobile-link-reparacion-cnc",
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const svcPinned = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (l) => {
    setOpen(false);
    setSvcOpen(false);
    svcPinned.current = false;
    if (l.to) {
      navigate(l.to);
      return;
    }
    const el = document.getElementById(l.id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleSvc = () => {
    svcPinned.current = !svcPinned.current;
    setSvcOpen(svcPinned.current);
  };
  const hoverSvc = (entering) => {
    if (entering) setSvcOpen(true);
    else if (!svcPinned.current) setSvcOpen(false);
  };

  const linkClass = `px-4 py-2 text-sm font-medium transition-colors ${
    scrolled ? "text-slate-700 hover:text-[#00B4D8]" : "text-white/90 hover:text-[#00B4D8]"
  }`;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-10 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => go({ id: "inicio" })}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <div
            className={`transition-all duration-300 ${
              scrolled ? "" : "bg-white px-3 py-1.5 shadow-lg"
            }`}
            style={{ borderRadius: "2px" }}
          >
            <img
              src={LOGO_URL}
              alt="Guarda Flex"
              className={`w-auto transition-all duration-300 ${scrolled ? "h-11" : "h-9"}`}
            />
          </div>
          <span
            className={`font-display font-black text-xl tracking-tight leading-none hidden sm:inline transition-colors duration-300 ${
              scrolled ? "" : "drop-shadow-md"
            }`}
          >
            <span className={scrolled ? "text-[#0A1B35]" : "text-white"}>GUARDA</span>
            <span className={scrolled ? "text-slate-600" : "text-slate-300"}> FLEX</span>
            <sup className={`ml-0.5 text-[10px] font-bold ${scrolled ? "text-slate-500" : "text-slate-300"}`}>™</sup>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) =>
            l.dropdown ? (
              <div
                key={l.id}
                className="relative"
                onMouseEnter={() => hoverSvc(true)}
                onMouseLeave={() => hoverSvc(false)}
              >
                <button
                  onClick={toggleSvc}
                  data-testid="nav-dropdown-servicios"
                  aria-expanded={svcOpen}
                  aria-haspopup="true"
                  className={`${linkClass} inline-flex items-center gap-1.5`}
                >
                  {l.label}
                  <ChevronDown
                    size={13}
                    className={`transition-transform duration-200 ${svcOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 transition-all duration-200 ${
                    svcOpen
                      ? "opacity-100 visible translate-y-0"
                      : "opacity-0 invisible -translate-y-1"
                  }`}
                >
                  <div
                    data-testid="nav-submenu-servicios"
                    className="w-64 bg-white border border-slate-200 shadow-[0_20px_40px_-16px_rgba(10,27,53,0.35)] overflow-hidden"
                    style={{ borderRadius: "4px" }}
                  >
                    {servicePages.map((p) => (
                      <button
                        key={p.to}
                        onClick={() => go(p)}
                        data-testid={p.testid}
                        className="w-full text-left px-5 py-3.5 hover:bg-slate-50 transition-colors border-l-2 border-transparent hover:border-[#00B4D8] group/item"
                      >
                        <span className="block text-sm font-semibold text-[#0A1B35] group-hover/item:text-[#0096B4] transition-colors">
                          {p.label}
                        </span>
                        <span className="block text-[11px] text-slate-500 mt-0.5">
                          {p.desc}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <button
                key={l.id}
                onClick={() => go(l)}
                data-testid={`nav-link-${l.id}`}
                className={linkClass}
              >
                {l.label}
              </button>
            )
          )}
          <button
            onClick={() => go({ id: "contacto" })}
            data-testid="nav-cta"
            className="gf-btn-primary ml-4"
          >
            Cotizar Ahora
          </button>
        </nav>

        <button
          onClick={() => {
            setOpen((s) => !s);
            setSvcOpen(false);
            svcPinned.current = false;
          }}
          className={`lg:hidden p-2 ${scrolled ? "text-[#0A1B35]" : "text-white"}`}
          data-testid="nav-mobile-toggle"
          aria-label="Abrir menú"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200 px-6 py-6 flex flex-col gap-1"
        >
          {links.map((l) =>
            l.dropdown ? (
              <div key={l.id} className="border-b border-slate-100">
                <button
                  onClick={toggleSvc}
                  data-testid="nav-mobile-servicios-toggle"
                  aria-expanded={svcOpen}
                  className="w-full flex items-center justify-between py-3 text-slate-800 font-medium"
                >
                  {l.label}
                  <ChevronDown
                    size={16}
                    className={`text-[#00B4D8] transition-transform duration-200 ${svcOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {svcOpen && (
                  <div className="ml-1 mb-2 pl-4 border-l-2 border-[#00B4D8]/40 flex flex-col">
                    {servicePages.map((p) => (
                      <button
                        key={p.to}
                        onClick={() => go(p)}
                        data-testid={p.mobileTestid}
                        className="text-left py-2.5"
                      >
                        <span className="block text-sm font-semibold text-[#0A1B35]">
                          {p.label}
                        </span>
                        <span className="block text-[11px] text-slate-500">
                          {p.desc}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                key={l.id}
                onClick={() => go(l)}
                data-testid={`nav-mobile-link-${l.id}`}
                className="text-left py-3 text-slate-800 font-medium border-b border-slate-100"
              >
                {l.label}
              </button>
            )
          )}
          <button
            onClick={() => go({ id: "contacto" })}
            className="gf-btn-primary mt-4 justify-center"
            data-testid="nav-mobile-cta"
          >
            Cotizar Ahora
          </button>
        </div>
      )}
    </header>
  );
}
